<template>
  <div class="chat-msg-item llm-stream-message" :class="{ 'is-mine': mine }">
    <div class="chat-msg-normal" :class="{ 'chat-msg-mine': mine }">
      <div class="head-image" v-if="!mine">
        <head-image :name="displayName" :size="38" :url="headImage" :id="msgInfo.sendId || 0" />
      </div>
      <div class="chat-msg-content">
        <div class="chat-msg-top" v-if="showHeader">
          <span class="author">{{ displayName }}</span>
          <span class="meta" v-if="isStreaming">
            <span class="dot dot-pulse"></span>
            <span>{{ streamingLabel }}</span>
          </span>
          <span class="meta error" v-else-if="errorMessage">
            <i class="el-icon-warning-outline"></i>
            <span>{{ errorMessage }}</span>
          </span>
        </div>
        <div class="chat-msg-bottom">
          <div ref="chatMsgBox" class="chat-msg-text">
            <roo-code-task-message
              v-if="isStructured"
              ref="taskMessage"
              :stream-id="streamId"
              :msg-info="msgInfo"
              :task-mode="taskMode"
              :show-metrics="showMetrics"
              :structured-chunks="structuredChunks"
              @need-scroll="scrollToBottom"
              @task-completed="onTaskCompleted"
              @task-error="onTaskError"
            />

            <div v-else>
              <loading-dots v-if="isStreaming && !textContent" />
              <roo-message-wrapper
                v-else
                ref="textRenderer"
                :markdown="textContent"
                class-name="roo-message-content"
              />
            </div>
          </div>
        </div>
        <div class="chat-msg-footer" v-if="isStreaming && !errorMessage">
          <span class="dot dot-pulse"></span>
          <span>{{ streamingFooter }}</span>
          <span v-if="startedAt" class="duration">{{ durationText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import LoadingDots from "../common/LoadingDots.vue";
import RooCodeTaskMessage from "./RooCodeTaskMessage.vue";
import RooMessageWrapper from "../message/RooMessageWrapper.vue";

const STREAMING_TEXT = "正在生成";

export default {
  name: "LLMStreamMessage",
  components: {
    HeadImage,
    LoadingDots,
    RooCodeTaskMessage,
    RooMessageWrapper,
  },
  props: {
    streamId: {
      type: String,
      required: true,
    },
    msgInfo: {
      type: Object,
      required: true,
    },
    mine: {
      type: Boolean,
      default: false,
    },
    headImage: {
      type: String,
      default: "",
    },
    showName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      taskMode: "code",
      showMetrics: false,
      isStreaming: false,
      isStructured: false,
      textContent: "",
      structuredChunks: [],
      processedChunkKeys: new Set(),
      startedAt: null,
      completedAt: null,
      errorMessage: "",
    };
  },
  computed: {
    displayName() {
      return this.showName || this.msgInfo.sendNickName || "AI 助手";
    },
    showHeader() {
      return !this.mine;
    },
    streamingLabel() {
      return STREAMING_TEXT;
    },
    streamingFooter() {
      return STREAMING_TEXT + "…";
    },
    textMessage() {
      return {
        content: this.textContent,
        streaming: this.isStreaming,
        format: "markdown",
      };
    },
    durationText() {
      if (!this.startedAt) return "";
      const end = this.completedAt || Date.now();
      const seconds = Math.floor((end - this.startedAt) / 1000);
      if (seconds <= 0) return "";
      return `${seconds}s`;
    },
  },
  methods: {
    addChunk(rawChunk) {
      const parsed = this.parseChunk(rawChunk);
      if (!parsed) return;

      if (!this.isStreaming) {
        this.startStream();
      }

      if (parsed.kind === "structured") {
        this.handleStructuredChunk(parsed.data);
      } else if (parsed.kind === "text") {
        this.handleTextChunk(parsed.data);
      }
    },

    parseChunk(chunk) {
      if (chunk == null) return null;

      if (typeof chunk === "string") {
        const trimmed = chunk.trim();
        if (!trimmed) {
          return { kind: "text", data: "" };
        }
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === "object" && parsed.type) {
            return { kind: "structured", data: parsed };
          }
        } catch (error) {
          // ignore JSON parse errors, treat as plain text
        }
        return { kind: "text", data: chunk };
      }

      if (typeof chunk === "object") {
        if (chunk.type) {
          return { kind: "structured", data: chunk };
        }
        if (typeof chunk.content === "string") {
          return { kind: "text", data: chunk.content };
        }
      }

      return { kind: "text", data: String(chunk) };
    },

    handleTextChunk(text) {
      if (typeof text !== "string" || !text) return;
      this.isStructured = false;
      this.textContent += text;
      // RooMessageWrapper 通过 watch markdown prop 自动更新
      this.scrollToBottom();
    },

    handleStructuredChunk(chunk) {
      const chunkKey = this.getChunkKey(chunk);
      if (chunkKey && this.processedChunkKeys.has(chunkKey)) {
        return;
      }

      if (chunkKey) {
        this.processedChunkKeys.add(chunkKey);
      }

      this.isStructured = true;
      this.structuredChunks = [...this.structuredChunks, chunk];

      this.$nextTick(() => {
        const taskMessage = this.$refs.taskMessage;
        if (taskMessage && typeof taskMessage.handleChunk === "function") {
          taskMessage.handleChunk(chunk);
        }
      });

      this.scrollToBottom();
    },

    getChunkKey(chunk) {
      if (!chunk || typeof chunk !== "object") return null;
      const candidateKeys = ["id", "ts", "sequence", "timestamp"];
      for (const key of candidateKeys) {
        const value = chunk[key];
        if (value !== undefined && value !== null) {
          if (chunk.partial) {
            const snippet = typeof chunk.content === "string"
              ? chunk.content.slice(-10)
              : JSON.stringify(chunk.content || "").slice(-10);
            return `${key}-${value}-partial-${snippet}`;
          }
          return `${key}-${value}`;
        }
      }
      if (chunk.type) {
        const status = chunk.partial ? "partial" : "final";
        const preview = typeof chunk.content === "string"
          ? chunk.content.slice(-10)
          : JSON.stringify(chunk.content || "").slice(-10);
        return `${chunk.type}-${status}-${preview}`;
      }
      try {
        return JSON.stringify(chunk);
      } catch (error) {
        return null;
      }
    },

    startStream() {
      this.isStreaming = true;
      this.startedAt = Date.now();
      this.completedAt = null;
      this.errorMessage = "";
    },

    endStream() {
      this.isStreaming = false;
      this.completedAt = Date.now();
      // RooMessageWrapper 不需要显式调用 finishStreaming
    },

    handleError(error) {
      const message =
        typeof error === "string" ? error : error?.error || "流式传输错误";
      this.errorMessage = message;
      this.isStreaming = false;
      this.completedAt = Date.now();
      this.$emit("error", { streamId: this.streamId, error: message });
    },

    onTaskCompleted(payload) {
      this.endStream();
      this.$emit("completed", {
        streamId: this.streamId,
        content: payload?.content,
        duration:
          this.completedAt && this.startedAt
            ? this.completedAt - this.startedAt
            : 0,
      });
    },

    onTaskError(payload) {
      this.handleError(payload?.error || "智能体执行失败");
    },


    scrollToBottom() {
      this.$emit("need-scroll");
    },

    clear() {
      this.isStreaming = false;
      this.isStructured = false;
      this.textContent = "";
      this.structuredChunks = [];
      this.processedChunkKeys = new Set();
      this.startedAt = null;
      this.completedAt = null;
      this.errorMessage = "";

      if (this.$refs.taskMessage && typeof this.$refs.taskMessage.clear === "function") {
        this.$refs.taskMessage.clear();
      }
      // RooMessageWrapper 通过 textContent 的响应式更新来清空
    },
  },
};
</script>

<style lang="scss" scoped>
.llm-stream-message {
  .chat-msg-normal {
    position: relative;
    padding-left: 48px;
    margin-top: 12px;

    &.chat-msg-mine {
      padding-left: 0;
      padding-right: 48px;
      text-align: right;

      .chat-msg-content {
        text-align: right;
      }
    }

    .head-image {
      position: absolute;
      width: 38px;
      height: 38px;
      top: 0;
      left: 0;
    }

    .chat-msg-content {
      text-align: left;

      .chat-msg-top {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 6px;
        font-size: 13px;
        color: var(--im-text-color-light, #909399);

        .author {
          font-weight: 600;
          color: var(--im-text-color, #303133);
        }

        .meta {
          display: inline-flex;
          align-items: center;
          gap: 4px;

          &.error {
            color: #ff7875;
          }
        }

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--im-color-primary, #409eff);
        }

        .dot-pulse {
          animation: pulse 1.6s ease-in-out infinite;
        }
      }

      .chat-msg-bottom {
        display: inline-block;
        max-width: min(640px, 80vw);

        .chat-msg-text {
          position: relative;
          padding: 16px;
          border-radius: 12px;
          background: var(--im-background, #f5f7fa);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
          font-size: 14px;
          line-height: 1.6;
          word-break: break-word;

          ::v-deep .roo-message-wrapper {
            // Roo Renderer 使用自己的样式
            // 如果需要覆盖，在这里添加
          }
        }
      }

      .chat-msg-footer {
        margin-top: 8px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: var(--im-text-color-light, #909399);

        .dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--im-color-primary, #409eff);
        }

        .duration {
          font-variant-numeric: tabular-nums;
          opacity: 0.85;
        }
      }
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.85);
  }
}
</style>
